import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import Session from 'supertokens-web-js/recipe/session';

import { environment } from '../../environments/environment';

type StoredSubscriptionSyncState = {
  signature: string;
  userId: string;
};

const PUSH_SUBSCRIPTION_SYNC_STORAGE_KEY = 'nt25.pushSubscriptionSync';

@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  private http = inject(HttpClient);

  async init(): Promise<void> {
    await this.subscribe();
  }

  async subscribe(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      return null;
    }

    if (!environment.PWA_PUBLIC_VAPID_KEY) {
      return null;
    }

    if (Notification.permission === 'denied') {
      return null;
    }

    try {
      const registration = await this.getRegistration();
      const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (permission !== 'granted') {
        return null;
      }

      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(environment.PWA_PUBLIC_VAPID_KEY)
        }));

      const subscriptionData = subscription.toJSON();
      if (!subscriptionData.endpoint) {
        return subscription;
      }

      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.debug('[PushNotificationsService] Skipping subscription sync because no active session was found.');
        return subscription;
      }

      const signature = this.getSubscriptionSignature(subscriptionData);
      if (this.isSubscriptionAlreadySynced(userId, signature)) {
        console.debug('[PushNotificationsService] Subscription already synced. Skipping POST.', {
          userId,
          endpoint: subscriptionData.endpoint
        });
        return subscription;
      }

      await firstValueFrom(this.http.post('/api/notifications/subscribe', subscriptionData, { withCredentials: true }));
      this.storeSubscriptionSyncState({ userId, signature });

      console.info('[PushNotificationsService] Subscription synced.', {
        userId,
        endpoint: subscriptionData.endpoint
      });

      return subscription;
    } catch (error) {
      console.warn('Push notifications subscription failed', error);
      return null;
    }
  }

  async unsubscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      if (!registration) {
        return false;
      }

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        return false;
      }

      const unsubscribed = await subscription.unsubscribe();
      if (unsubscribed) {
        this.clearStoredSubscriptionSyncState();
      }

      return unsubscribed;
    } catch (error) {
      console.warn('Push notifications unsubscription failed', error);
      return false;
    }
  }

  private isSupported(): boolean {
    return (
      environment.PUSH_NOTIFICATIONS_ENABLED &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  private async getRegistration(): Promise<ServiceWorkerRegistration> {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) {
      return existing;
    }

    return navigator.serviceWorker.register('/sw.js', { scope: '/' });
  }

  private async getCurrentUserId(): Promise<string | null> {
    const hasSession = await Session.doesSessionExist().catch(() => false);
    if (!hasSession) {
      return null;
    }

    return Session.getUserId().catch(() => null);
  }

  private getSubscriptionSignature(subscription: PushSubscriptionJSON): string {
    return JSON.stringify({
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime ?? null,
      keys: {
        auth: subscription.keys?.['auth'] ?? null,
        p256dh: subscription.keys?.['p256dh'] ?? null
      }
    });
  }

  private isSubscriptionAlreadySynced(userId: string, signature: string): boolean {
    const stored = this.getStoredSubscriptionSyncState();
    return stored?.userId === userId && stored.signature === signature;
  }

  private getStoredSubscriptionSyncState(): StoredSubscriptionSyncState | null {
    const rawState = localStorage.getItem(PUSH_SUBSCRIPTION_SYNC_STORAGE_KEY);
    if (!rawState) {
      return null;
    }

    try {
      return JSON.parse(rawState) as StoredSubscriptionSyncState;
    } catch {
      this.clearStoredSubscriptionSyncState();
      return null;
    }
  }

  private storeSubscriptionSyncState(state: StoredSubscriptionSyncState): void {
    localStorage.setItem(PUSH_SUBSCRIPTION_SYNC_STORAGE_KEY, JSON.stringify(state));
  }

  private clearStoredSubscriptionSyncState(): void {
    localStorage.removeItem(PUSH_SUBSCRIPTION_SYNC_STORAGE_KEY);
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(rawData.length));

    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}
